import { ShoppingCart } from "lucide-react";
import { useState } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
} from "@/components/ui/item";
import { Label } from "@/components/ui/label";
import { searchSectorsPath } from "@/routes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // No default sectors - selection is done via async search
  onConfirm: (sectorId: number, startApproval: boolean) => void;
  isSubmitting?: boolean;
}

export function CreateDirectPurchaseDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting = false,
}: Props) {
  const [selectedSector, setSelectedSector] = useState<AsyncOption | null>(
    null
  );
  const [startApproval, setStartApproval] = useState(true);

  const handleConfirm = () => {
    if (!selectedSector?.value) return;
    onConfirm(Number(selectedSector.value), startApproval);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Crear compra directa
          </DialogTitle>
          <DialogDescription>
            Se creará una Solicitud de Compra con los datos de esta factura
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sector">Sector u Obra</Label>
            <AsyncCombobox
              value={selectedSector}
              onChange={(opt) => setSelectedSector(opt)}
              loadOptions={async (inputValue: string) => {
                try {
                  const params = new URLSearchParams();
                  if (inputValue) params.set("term", inputValue);
                  const url = `${searchSectorsPath()}?${params.toString()}`;
                  const resp = await fetch(url, {
                    headers: { Accept: "application/json" },
                  });
                  if (!resp.ok) return [];
                  const data = (await resp.json()) as { value: number | string; label: string }[];
                  return data.map((d) => ({ value: String(d.value), label: d.label }));
                } catch (_err) {
                  return [];
                }
              }}
              defaultOptions={false}
              cacheOptions={true}
              placeholder="Buscar sector u obra..."
            />
            <p className="text-xs text-muted-foreground">
              Selecciona el sector o obra al que pertenece esta compra
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="start-approval"
              checked={startApproval}
              onCheckedChange={(checked) => setStartApproval(checked === true)}
            />
            <Label
              htmlFor="start-approval"
              className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Iniciar aprobación automáticamente
            </Label>
          </div>

          <ItemGroup>
            <Item variant="muted" size="sm">
              <ItemContent>
                <ItemTitle>¿Qué pasará al confirmar?</ItemTitle>
                <ItemDescription>
                  Se ejecutarán los siguientes pasos automáticamente
                </ItemDescription>
              </ItemContent>
            </Item>
            <ItemSeparator />
            <Item variant="default" size="sm">
              <ItemContent>
                <ItemTitle>✓ Se crea la Solicitud de Compra</ItemTitle>
                <ItemDescription>
                  Con los ítems y datos de esta factura
                </ItemDescription>
              </ItemContent>
            </Item>
            <ItemSeparator />
            <Item variant="default" size="sm">
              <ItemContent>
                <ItemTitle>✓ La factura queda vinculada</ItemTitle>
                <ItemDescription>
                  Pasará a estado &ldquo;Asignada&rdquo; automáticamente
                </ItemDescription>
              </ItemContent>
            </Item>
            <ItemSeparator />
            {startApproval && (
              <>
                <Item variant="default" size="sm">
                  <ItemContent>
                    <ItemTitle>✓ Inicia el flujo de aprobaciones</ItemTitle>
                    <ItemDescription>
                      Según el circuito configurado para el sector elegido
                    </ItemDescription>
                  </ItemContent>
                </Item>
                <ItemSeparator />
              </>
            )}
            <Item variant="muted" size="sm">
              <ItemContent>
                <ItemTitle>Limitaciones de la compra directa</ItemTitle>
                <ItemDescription>
                  No podrás agregar cotizaciones ni ver comparativa de precios. Solo se admiten aprobaciones, comentarios y remitos
                </ItemDescription>
              </ItemContent>
            </Item>
          </ItemGroup>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedSector?.value || isSubmitting}
          >
            {isSubmitting ? "Creando..." : "Crear compra directa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
